// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  CompanySize,
  FreeformSegment,
  MultiLevel,
  Segment,
  SideSegment,
  Territory,
} from "../components/Segments";
import Locale from "../components/Segments/Components/Locale/Locale";
import UserList from "../components/Segments/Components/UserList";
import { AuthContext } from "../contexts/auth-context";
import { isFeatureFlagged } from "./feature-flag";

const isSegFlagged = (name) => {
  return !isFeatureFlagged(`segment-${name}`);
};

export const setSegTag = (search, segment, tag) => {
  const { ...newSearch } = search;
  // if (segment !== 'ops') {
  if (Object.hasOwn(newSearch, segment)) {
    const id = newSearch[segment].indexOf(tag);
    if (id > -1) {
      const updatedSeg = [...newSearch[segment]];

      updatedSeg.splice(id, 1);

      newSearch[segment] = updatedSeg;
    } else {
      newSearch[segment] = [...newSearch[segment], tag];
    }
  } else {
    newSearch[segment] = [tag];
  }
  return newSearch;
};

export const replaceSegTag = (search, segment, tag) => {
  let { ...newSearch } = search;
  if (!tag || tag.length === 0) {
    delete newSearch[segment];
    newSearch = { ...newSearch };
  } else {
    newSearch = { ...newSearch, [segment]: [tag] };
  }
  return newSearch;
};

export const setSegList = (search, params = []) => {
  let newSearch = { ...search };
  params.forEach((p) => {
    if (p["replace"]) {
      newSearch = replaceSegTag(newSearch, p.name, p.value);
    } else {
      newSearch = setSegTag(newSearch, p.name, p.value);
    }
  });
  return newSearch;
};

export const swapSegs = (search, segment, tag, ops) => {
  const [seg, op] = segment.split(":");
  if (op && ops.includes(op.toLowerCase())) {
    return [
      ...ops
        .filter((o) => op !== o)
        .map((o) => {
          const iSeg = `${seg}:${o}`;
          if (Object.hasOwn(search, iSeg) && search[iSeg].includes(tag)) {
            return {
              name: iSeg,
              value: tag,
            };
          }
          return undefined;
        })
        .filter((o) => typeof o !== "undefined"),
      { name: segment, value: tag },
    ];
  }
};

export const nonSubSegs = ["territory", "new_clinics", "location_list"];

const internalSegments = [
  {
    name: "treatment_categories",
    title: "Treatment Types",
    color: "red",
    op: "or",
    canReduce: false,
    component: SideSegment,
    show_tag: true,
  },
  {
    name: "market_classification",
    title: "Service Classifications",
    color: "blue",
    op: "or",
    canReduce: false,
    component: SideSegment,
    show_tag: true,
  },
  {
    name: "catalog_data",
    title: "Manufacturers and Brands",
    color: "orange",
    op: "or",
    canReduce: true,
    component: MultiLevel,
    show_tag: false,
    mapped_segments: ["manufacturers", "brands_used", "products"],
  },
  {
    name: "manufacturers",
    title: "Manufacturers",
    color: "orange",
    op: "or",
    canReduce: true,
    component: () => <></>,
    cComponent: SideSegment,
    show_tag: true,
  },
  {
    name: "brands_used",
    title: "Brands Used",
    color: "orange",
    op: "or",
    canReduce: true,
    component: () => <></>,
    cComponent: SideSegment,
    show_tag: true,
  },
  {
    name: "company_size",
    title: "Company Size",
    color: "purple",
    op: false,
    canReduce: false,
    component: CompanySize,
    show_tag: true,
  },
  {
    name: "territory",
    title: "Territory",
    color: "green",
    op: false,
    canReduce: true,
    component: Territory,
    show_tag: true,
  },
  {
    name: "registers",
    title: "Professional Registry",
    color: "purple",
    show_tag: true,
    op: false,
    canReduce: true,
    component: ({ segment, ...props }) => {
      const opts = segment.strings
        .map((st) => {
          if (st === "none") {
            return {
              name: "Unregistered",
              value: st,
            };
          } else {
            return {
              name: st.toUpperCase(),
              value: st,
            };
          }
        })
        .sort((a, b) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        });

      return <SideSegment segment={{ ...segment, strings: [] }} {...props} customOpts={opts} />;
    },
    help: "Find locations that are members of these registries",
  },
  {
    name: "location_list",
    title: "My Lists",
    show_tag: false,
    color: "purple",
    op: false,
    help: "Select locations",
    component: UserList,
  },
  // {
  //   name: "newer_than",
  //   title: "Days since location added",
  //   color: "purple",
  //   op: false,
  //   canReduce: false,
  //   component: DropdownWithFreeform,
  //   show_tag: false,
  //   options: ["20", "40", "80"],
  //   help: "Shows locations added to the index up to the number of days selected"
  // },
  {
    name: "data_source_name",
    title: "From Data Source(s)",
    show_tag: true,
    color: "purple",
    op: false,
    canReduce: true,
    component: SideSegment,
  },
  {
    name: "area_id",
    title: "Country Select",
    show_tag: true,
    component: ({ segment, ...props }) => {
      const { t } = useTranslation();
      return (
        <Segment
          {...props}
          segment={{ ...segment, strings: [] }}
          customOpts={segment.strings.map((s) => ({
            ...s,
            label: t(s.label.toLowerCase().replace("/", ".")),
          }))}
          showFilter={false}
        />
      );
    },
    op: ["or"],
  },
  {
    name: "locale",
    title: "Place or Postcode",
    show_tag: false,
    op: false,
    component: ({ ...props }) => {
      const {
        subscription: {
          settings: { locales = ["gb"] },
        },
      } = useContext(AuthContext);
      return <Locale {...props} country={locales} />;
    },
    options: ["postcode", "place", "locality", "region", "district"],
    help: "Search by postcode, or by city/town/locality",
    allowClear: true,
  },
  {
    name: "location_id",
    title: "Location ID",
    show_tag: false,
    op: "or",
    component: FreeformSegment,
    meta: true,
    allowClear: true,
  },
  {
    name: "google_place_id",
    title: "Google Place ID",
    show_tag: false,
    op: "or",
    component: FreeformSegment,
    meta: true,
    allowClear: true,
  },
  {
    name: "provider_id",
    title: "Provider ID",
    show_tag: false,
    op: "or",
    component: FreeformSegment,
    meta: true,
    allowClear: true,
  },
  {
    name: "internal_id",
    title: "Internal ID",
    show_tag: false,
    op: "or",
    component: FreeformSegment,
    meta: true,
    allowClear: true,
  },
  // {
  //   name: "client_status",
  //   title: "Client Status",
  //   show_tag: false,
  //   op: "or",
  //   canReduce: false,
  //   component: () => <></>,
  // }
];

export const getSegColour = (name) => {
  const seg = internalSegments.find((seg) => seg.name === name);
  if (!seg) {
    return "purple";
  } else {
    return seg.color;
  }
};

export const returnSegments = (segs = [], include = false) => {
  return (() => {
    if (include) {
      return internalSegments.filter((segment) => segs.includes(segment.name));
    }
    return internalSegments.filter((segment) => !segs.includes(segment.name) && !segment.meta);
  })().filter((segment) => isSegFlagged(segment.name));
};

// TODO bring this in from the API instead of hard-coding
export const segments = returnSegments();

export const accountSegments = [
  ...returnSegments(nonSubSegs).filter((s) => s.name !== "catalog_data"),
];

export const shornSeg = (name) => {
  const [seg] = name.split(":");
  return seg;
};
export const opTexts = {
  or: "Any",
  and: "Require",
  not: "Hide",
};
